import Vue from 'vue';

// Estado das sheets do fluxo de download (qualidade, confirmar remoção/
// cancelamento, confirmar rede móvel), num único observable de módulo.
//
// Por que singleton e não data de componente: sheet é, por definição, um
// elemento único na tela. Com o estado no componente, cada consumidor do
// downloadActions precisava montar as sheets no próprio template — e como hoje
// há mais de um consumidor vivo ao mesmo tempo (o ícone no menu lateral e o
// botão na página da aula), existiam instâncias duplicadas de backdrop e de
// trava de scroll do body. Aqui o <DownloadSheets /> é montado UMA vez no
// AppLayout e qualquer código chama as funções abaixo.
//
// A API é por Promise: quem pergunta dá await na resposta em vez de encadear
// callback. `null`/`false` = usuário desistiu.
export const downloadSheets = Vue.observable({
  quality: {
    visible: false,
    mode: null,
    initialSelected: null,
    wifiDefaultQuality: null
  },
  confirm: {
    visible: false,
    itemName: null,
    icon: 'cloud_off',
    title: 'Remover dos downloads?',
    description: 'Você não conseguirá acessar esse conteúdo quando estiver offline.',
    dismissLabel: 'Cancelar',
    confirmLabel: 'Remover',
    confirmIcon: 'delete_outline'
  },
  cellular: {
    visible: false,
    itemName: null
  },
  modules: {
    visible: false,
    groups: [],
    // Aba "Materiais" (arquivos complementares). courseId habilita o fetch do
    // DRM de PDF; filesCount > 0 é o que faz a sheet exibir as abas.
    courseId: null,
    filesCount: 0,
    // Estado agregado do curso ('idle' | 'downloading' | 'downloaded'). A sheet
    // abre em QUALQUER estado — é a única porta dos materiais no app —, então
    // ela precisa saber qual ação oferecer no rodapé.
    status: 'idle'
  }
});

// Resolvers ficam FORA do observable: são funções, não têm por que ser
// reativas, e deixá-las lá faria o Vue instrumentá-las à toa.
const resolvers = { quality: null, confirm: null, cellular: null, modules: null };

function open (key, patch) {
  // Sheet anterior pendurada (não deveria acontecer, mas é barato defender):
  // resolve como desistência antes de abrir a nova, senão a Promise antiga
  // nunca settla e quem deu await fica preso pra sempre.
  if (resolvers[key]) {
    const stale = resolvers[key];
    resolvers[key] = null;
    stale(null);
  }
  Object.assign(downloadSheets[key], patch, { visible: true });
  return new Promise(resolve => { resolvers[key] = resolve; });
}

function close (key, value) {
  downloadSheets[key].visible = false;
  const resolve = resolvers[key];
  resolvers[key] = null;
  if (resolve) resolve(value);
}

// → 'low' | 'medium' | 'high' | null (desistiu)
export function askQuality ({ mode, initialSelected, wifiDefaultQuality }) {
  return open('quality', { mode, initialSelected, wifiDefaultQuality: wifiDefaultQuality || null });
}

// → true | false. `overrides` troca textos/ícones (é assim que a mesma sheet
// serve pra "remover baixado" e pra "cancelar download em andamento").
export function askConfirm (overrides = {}) {
  return open('confirm', Object.assign({
    itemName: null,
    icon: 'cloud_off',
    title: 'Remover dos downloads?',
    description: 'Você não conseguirá acessar esse conteúdo quando estiver offline.',
    dismissLabel: 'Cancelar',
    confirmLabel: 'Remover',
    confirmIcon: 'delete_outline'
  }, overrides));
}

// → { persist: boolean } | null (desistiu)
export function askCellular (itemName) {
  return open('cellular', { itemName: itemName || null });
}

// → array de grupos escolhidos | null (desistiu). Recebe TODOS os módulos; a
// sheet marca todos por padrão e devolve só os selecionados.
//
// `opts.filesCount` > 0 acrescenta a aba "Materiais" (arquivos complementares
// do curso) — no app ela substitui a entrada "Downloads" do resumo, que abria
// o slideover. Baixar material NÃO resolve a Promise: a resposta continua
// sendo só sobre módulos.
export function askModules (groups, opts) {
  return open('modules', {
    groups: groups || [],
    courseId: (opts && opts.courseId) || null,
    filesCount: (opts && opts.filesCount) || 0,
    status: (opts && opts.status) || 'idle'
  });
}

export const resolveQuality = (value) => close('quality', value);
export const resolveConfirm = (value) => close('confirm', value);
export const resolveCellular = (value) => close('cellular', value);
export const resolveModules = (value) => close('modules', value);
